import { useState } from 'preact/hooks';
import {
  createWallet,
  importWallet,
  forgetWallet,
  wallet,
  send,
  receipts,
  downloadReceipts,
  makePrivate,
  notes,
  notesZat,
  noteableZat,
  fund,
  withdraw,
  withdrawalAddress,
  siteInfoChanged,
  acceptSiteLog,
} from '../wallet.js';
import { useStore, refreshAccount, totalZat, coinAccountsZat } from '../store.js';
import { api, ApiError, type TokenSummary } from '../api.js';
import { QR, copy, toast, Avatar } from '../components/bits.js';
import { Receipt } from '../components/Receipt.js';
import { zec, parseZec, tokens, usd } from '../format.js';
import { verifyMerkleProof } from '../../../src/core/merkle.js';
import { verifyCheckpointSig } from '../../../src/core/log.js';
import { canonicalize } from '../../../src/core/canonical.js';
import { isValidAddress, isValidWithdrawalAddress } from '../../../src/core/ledger.js';
import { utf8ToBytes } from '@noble/hashes/utils.js';

export function WalletPage() {
  const s = useStore();
  const w = wallet();
  const [backup, setBackup] = useState<string | null>(null);
  if (!w) return <Onboarding onCreated={(secret) => setBackup(secret)} />;
  if (backup) return <Backup secret={backup} onDone={() => setBackup(null)} />;
  const acc = s.account;
  if (!acc) return <div class="empty">Loading your wallet…</div>;
  if (!acc.exists)
    return (
      <div class="empty">
        Opening your account…{' '}
        <button class="btn ghost" onClick={() => send({ type: 'register' }).then(() => refreshAccount())}>
          Retry
        </button>
      </div>
    );

  return (
    <div>
      <h1 class="page-title">Wallet</h1>
      {siteInfoChanged() && (
        <div class="notice bad" style={{ marginBottom: 16 }}>
          This site now runs a different log than the one your wallet first used. That happens when a test network is restarted, but it
          would also be how an impostor site looks. Check the announcement before trading.{' '}
          <button class="btn ghost" onClick={() => acceptSiteLog()}>
            I checked, use the new log
          </button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div class="big-balance">{zec(totalZat(), 8)} ZEC</div>
        <div class="muted">{usd(totalZat(), s.zecUsd)}</div>
      </div>
      <Privacy />
      <div class="grid2">
        <Deposit address={acc.depositAddress ?? null} />
        <Withdraw balance={BigInt(acc.zat ?? '0')} />
      </div>
      <Holdings />
      <Launches />
      <Receipts />
      <Proof />
      <Keys />
    </div>
  );
}

function Onboarding({ onCreated }: { onCreated: (secret: string) => void }) {
  const [mode, setMode] = useState<'choose' | 'import'>('choose');
  const [secret, setSecret] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div class="page-narrow">
      <h1 class="page-title">Your wallet</h1>
      <p class="lede">
        Your wallet is a key kept in this browser. It signs every trade and withdrawal, so nobody else can move your coins, including us. Your
        ZEC is held in the platform vault, and the vault's books are public.
      </p>
      {mode === 'choose' ? (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button
            class="btn brass"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setErr('');
              try {
                const w = await createWallet();
                onCreated(w.secretKey);
              } catch (x) {
                setErr((x as Error).message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? 'Creating…' : 'Create a wallet'}
          </button>
          <button class="btn ghost" onClick={() => setMode('import')}>
            I have a recovery key
          </button>
        </div>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr('');
            try {
              await importWallet(secret);
              toast('Wallet restored');
            } catch (x) {
              setErr((x as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <div class="field">
            <label for="sec">Recovery key</label>
            <div class="input">
              <textarea id="sec" value={secret} onInput={(e) => setSecret((e.target as HTMLTextAreaElement).value)} spellcheck={false} autocomplete="off" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button class="btn brass" disabled={busy}>
              Restore wallet
            </button>
            <button type="button" class="btn ghost" onClick={() => setMode('choose')}>
              Back
            </button>
          </div>
        </form>
      )}
      {err && (
        <div class="notice bad" style={{ marginTop: 16 }}>
          {err}
        </div>
      )}
    </div>
  );
}

function Backup({ secret, onDone }: { secret: string; onDone: () => void }) {
  const [ok, setOk] = useState(false);
  return (
    <div class="page-narrow">
      <h1 class="page-title">Save your recovery key</h1>
      <p class="lede">
        This key is the only way to get back into your wallet if you clear this browser or switch devices. Anyone who has it controls your
        funds. Write it down or store it in a password manager.
      </p>
      <div class="secret">{secret}</div>
      <div style={{ display: 'flex', gap: 12, margin: '12px 0 20px' }}>
        <button class="btn ghost" onClick={() => copy(secret)}>
          Copy key
        </button>
      </div>
      <label class="check">
        <input type="checkbox" checked={ok} onChange={(e) => setOk((e.target as HTMLInputElement).checked)} />
        <span>I saved my recovery key somewhere safe.</span>
      </label>
      <button class="btn brass block" style={{ marginTop: 16 }} disabled={!ok} onClick={onDone}>
        Continue to wallet
      </button>
    </div>
  );
}

/** Amounts offered as ready-made payment requests (ZIP 321): scan with Zashi, YWallet or Zingo and confirm. */
const PAY_AMOUNTS = ['0.05', '0.1', '0.5', '1'];

function Deposit({ address }: { address: string | null }) {
  const s = useStore();
  const [amount, setAmount] = useState<string | null>(null);
  const conf = s.info?.beta.minConfirmations ?? 3;
  const fast = BigInt(s.info?.beta.fastDepositMaxZat ?? '0');
  const incoming = s.account?.incoming ?? [];
  const isMock = s.info?.genesis.network === 'mock';
  const uri = address ? (amount ? `zcash:${address}?amount=${amount}` : address) : '';
  return (
    <section class="panel">
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Deposit ZEC</h2>
      {address ? (
        <>
          <div class="quick" style={{ margin: '0 0 10px' }} role="group" aria-label="Amount to request">
            <button class="chip" aria-pressed={amount === null} onClick={() => setAmount(null)}>
              Any amount
            </button>
            {PAY_AMOUNTS.map((a) => (
              <button class="chip" key={a} aria-pressed={amount === a} onClick={() => setAmount(a)}>
                {a} ZEC
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <QR text={uri} />
            <div style={{ flex: 1, minWidth: 200 }}>
              <div class="address">{address}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button class="btn ghost" onClick={() => copy(address)}>
                  Copy address
                </button>
                {amount && (
                  <a class="btn ghost" href={uri}>
                    Open in wallet app
                  </a>
                )}
              </div>
            </div>
          </div>
          {incoming.map((p, i) => (
            <div class="notice launch" style={{ margin: '12px 0 0' }} key={i}>
              {zec(p.zat, 8)} ZEC is on its way:{' '}
              {p.confirmations === 0
                ? `seen on the network, ready after ${p.needed === 1 ? 'the next block' : `${p.needed} blocks`} (about ${Math.ceil((p.needed * 75) / 60)} min).`
                : `${p.confirmations} of ${p.needed} blocks, ready in about ${Math.ceil(((p.needed - p.confirmations) * 75) / 60)} min.`}
            </div>
          ))}
          <p class="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Send ZEC from any shielded wallet, or scan the code with Zashi, YWallet or Zingo. You see it here as soon as it is on the network.{' '}
            {fast > 0n && conf > 1 ? `Up to ${zec(fast)} ZEC is fully yours after 1 block (about 75 s), more after ${conf} blocks.` : `It is fully yours after ${conf} block${conf === 1 ? '' : 's'}.`}{' '}
            During the beta, keep deposits under {zec(s.info?.beta.maxUserDepositZat ?? '0')} ZEC.
          </p>
          {isMock && (
            <button
              class="btn"
              style={{ marginTop: 12 }}
              onClick={async () => {
                await api.devDeposit(wallet()!.publicKey, 1);
                refreshAccount(300);
                toast('Added 1 test ZEC');
              }}
            >
              Add 1 test ZEC
            </button>
          )}
        </>
      ) : (
        <p class="muted">Your deposit address is being created. It appears here in a few seconds.</p>
      )}
    </section>
  );
}

/**
 * Where the wallet's ZEC sits. Deposits land in the main account; "Make
 * private" turns them into notes; trades draw on notes from a fresh account
 * per coin; "Collect" turns coin accounts' ZEC back into notes.
 */
/** Below this many notes in circulation, the wallet warns that privacy is weak. */
const LOW_COVER = 20;

function Privacy() {
  const s = useStore();
  const [busy, setBusy] = useState('');
  const main = BigInt(s.account?.zat ?? '0');
  const priv = notesZat();
  const coins = coinAccountsZat();
  const inCirculation = Object.values(s.info?.stats.notesOut ?? {}).reduce((a, b) => a + Number(b), 0);
  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    try {
      await fn();
      toast(
        what === 'private'
          ? 'Your ZEC is private now'
          : collectable.length > 1
            ? `Collected one coin account. Collect the other ${collectable.length - 1} later, not all at once.`
            : 'Collected into private notes',
      );
    } catch (x) {
      toast((x as Error).message, true);
    } finally {
      setBusy('');
      refreshAccount(100);
    }
  };
  // One coin account per click, picked at random: collecting them all in a row would show
  // anyone reading the log a burst of mints from accounts that belong together.
  const collectable = s.accounts.filter((a) => a.index !== 0 && noteableZat(BigInt(a.data.zat ?? '0')) > 0n);
  const collect = async () => {
    const a = collectable[Math.floor(Math.random() * collectable.length)];
    if (a) await makePrivate(a.index, noteableZat(BigInt(a.data.zat ?? '0')));
  };
  return (
    <section class="panel" style={{ marginBottom: 16 }}>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Privacy</h2>
      <p class="muted" style={{ fontSize: 13, margin: '0 0 12px', maxWidth: '70ch' }}>
        Every coin you trade uses its own account, paid from private notes. Nobody, including us, can link those accounts to each other or to
        your deposits. Make ZEC private well before you trade: the longer the gap, the harder it is to guess.
      </p>
      <table class="list">
        <tbody>
          <tr>
            <td>
              Main account <span class="muted">(deposits and withdrawals)</span>
            </td>
            <td class="r">{zec(main, 8)} ZEC</td>
            <td class="r">
              <button class="btn brass" disabled={!!busy || noteableZat(main) === 0n} onClick={() => act('private', () => makePrivate(0, noteableZat(main)))}>
                {busy === 'private' ? 'Making private…' : 'Make private'}
              </button>
            </td>
          </tr>
          <tr>
            <td>
              Private notes <span class="muted">({notes().length})</span>
            </td>
            <td class="r">{zec(priv, 8)} ZEC</td>
            <td />
          </tr>
          <tr>
            <td>Coin accounts</td>
            <td class="r">{zec(coins, 8)} ZEC</td>
            <td class="r">
              <button class="btn ghost" disabled={!!busy || collectable.length === 0} onClick={() => act('collect', collect)}>
                {busy === 'collect' ? 'Collecting…' : collectable.length > 1 ? `Collect one (${collectable.length})` : 'Collect'}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p class="muted" style={{ fontSize: 12, margin: '10px 0 0' }}>
        {inCirculation} private note{inCirculation === 1 ? '' : 's'} in circulation on the platform right now. More notes, more cover.
      </p>
      {inCirculation < LOW_COVER && (
        <div class="notice warn" style={{ marginTop: 10, fontSize: 13 }}>
          Few notes are in circulation, so privacy is weak for now: with little traffic, timing can link your accounts. It gets stronger as
          more people use the platform.
        </div>
      )}
      <p class="muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
        The log hides who you are from everyone reading it. Our server still sees your IP address when you trade; use Tor if that matters
        to you.
      </p>
    </section>
  );
}

function Withdraw({ balance }: { balance: bigint }) {
  const s = useStore();
  const [address, setAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const g = s.info?.genesis;
  const fee = g ? BigInt(g.withdrawFeeZat) : 0n;
  const min = g ? BigInt(g.minWithdrawZat) : 0n;
  const zat = parseZec(amount);
  const pending = s.account?.pending ?? [];
  // Notes top up the main account when it is short.
  const available = balance + notesZat();
  const addr = address.trim();
  const problem =
    zat === null && amount
      ? 'Enter an amount like 0.25'
      : zat !== null && zat > available
        ? 'More than your main account and private notes hold.'
        : zat !== null && zat > 0n && zat < min
          ? `The minimum is ${zec(min, 8)} ZEC.`
          : addr && g && isValidAddress(g.network, addr) && !isValidWithdrawalAddress(g.network, addr)
            ? 'Transparent (t…) addresses are not accepted: a payment to one is public on the blockchain. Use a shielded address (u1… or zs1…).'
            : addr && g && !isValidAddress(g.network, addr)
              ? 'That is not a valid Zcash address for this network.'
              : '';

  return (
    <section class="panel">
      <h2 style={{ fontSize: 18, marginBottom: 12 }}>Withdraw ZEC</h2>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!zat || !g) return;
          setBusy(true);
          setErr('');
          try {
            await fund(0, zat, balance);
            await withdraw(zat, addr, g.encKey);
            toast('Withdrawal requested');
            setAmount('');
            refreshAccount();
          } catch (x) {
            setErr(x instanceof ApiError && x.code === 'bad_address' ? 'That is not a valid Zcash address for this network.' : (x as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <div class="field">
          <label for="wa">To address</label>
          <div class="input">
            <input id="wa" value={address} onInput={(e) => setAddress((e.target as HTMLInputElement).value)} placeholder="u1… or zs1…" spellcheck={false} autocomplete="off" />
          </div>
        </div>
        <div class="field">
          <label for="wz">Amount</label>
          <div class="input">
            <input id="wz" inputMode="decimal" value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} placeholder="0.0" />
            <button type="button" class="chip" style={{ marginRight: 8 }} onClick={() => setAmount(zec(available, 8).replace(/ /g, ''))}>
              Max
            </button>
            <span class="unit">ZEC</span>
          </div>
          {problem ? (
            <span class="err">{problem}</span>
          ) : (
            <span class="hint">
              Network fee {zec(fee, 8)} ZEC.{zat && zat > fee ? ` You receive ${zec(zat - fee, 8)} ZEC.` : ''} The address is encrypted: only the payout
              service can read it, the public log never shows it.
            </span>
          )}
        </div>
        {err && (
          <div class="notice bad" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}
        <button class="btn brass block" disabled={busy || !zat || !!problem || !addr}>
          {busy ? 'Signing…' : 'Withdraw'}
        </button>
      </form>
      {pending.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {pending.map((p) => (
            <div class="notice" key={p.id} style={{ marginTop: 8 }}>
              {zec(p.sendZat, 8)} ZEC to {withdrawalAddress(p.commit)?.slice(0, 12) ?? 'a sealed address'}…:{' '}
              {p.needsApproval ? 'large withdrawal, waiting for a manual check (beta).' : 'sending, usually within a few minutes.'}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Holdings() {
  const s = useStore();
  // Coins are spread over accounts; add them up per coin.
  const byId = new Map<string, TokenSummary & { balance: string }>();
  for (const a of s.accounts)
    for (const t of a.data.tokens ?? []) {
      const prev = byId.get(t.id);
      byId.set(t.id, prev ? { ...t, balance: (BigInt(prev.balance) + BigInt(t.balance)).toString() } : t);
    }
  const list = [...byId.values()];
  return (
    <section style={{ marginTop: 32 }}>
      <h2 class="display" style={{ fontSize: 36, marginBottom: 8 }}>
        Your coins
      </h2>
      {list.length === 0 ? (
        <div class="empty">
          You don't hold any coins yet. <a href="#/">Find one to buy</a>
        </div>
      ) : (
        <table class="list">
          <thead>
            <tr>
              <th>Coin</th>
              <th class="r">Amount</th>
              <th class="r">Value</th>
            </tr>
          </thead>
          <tbody>
            {list.map((t) => (
              <tr key={t.id}>
                <td>
                  <a href={`#/token/${t.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                    <Avatar token={t} size={28} />
                    <strong>${t.symbol}</strong>
                  </a>
                </td>
                <td class="r">{tokens(t.balance)}</td>
                <td class="r">{zec(Math.round((Number(t.balance) / 1e6) * t.priceZat), 4)} ZEC</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Orders waiting for a launch minute to end, and what coins this wallet launched have earned it. */
function Launches() {
  const s = useStore();
  const orders = s.accounts.flatMap((a) => a.data.launchOrders ?? []);
  const created = s.accounts.flatMap((a) => a.data.created ?? []);
  if (!orders.length && !created.length) return null;
  return (
    <section style={{ marginTop: 32 }}>
      {orders.length > 0 && (
        <>
          <h2 class="display" style={{ fontSize: 36, marginBottom: 8 }}>
            Waiting for launch
          </h2>
          <table class="list">
            <tbody>
              {orders.map((o) => (
                <tr key={o.token}>
                  <td>
                    <a href={`#/token/${o.token}`}>${o.symbol}</a>
                  </td>
                  <td class="r">{zec(o.zat, 6)} ZEC</td>
                  <td class="r muted">fills at one price at {new Date(o.fillsAt).toLocaleTimeString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      {created.length > 0 && (
        <>
          <h2 class="display" style={{ fontSize: 36, margin: '24px 0 8px' }}>
            Coins you launched
          </h2>
          <p class="muted" style={{ marginTop: 0 }}>
            You earn {(s.info?.genesis.creatorBps ?? 0) / 100}% of every trade in your coins. It lands in the coin's account and counts in your balance.
          </p>
          <table class="list">
            <tbody>
              {created.map((c) => (
                <tr key={c.token}>
                  <td>
                    <a href={`#/token/${c.token}`}>${c.symbol}</a>
                  </td>
                  <td class="r">earned {zec(c.earnedZat, 6)} ZEC</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function Receipts() {
  const [open, setOpen] = useState(false);
  const list = receipts().slice().reverse();
  return (
    <section style={{ marginTop: 32 }}>
      <h2 class="display" style={{ fontSize: 36, marginBottom: 8 }}>
        Receipts
      </h2>
      <p class="muted" style={{ maxWidth: '62ch' }}>
        This browser keeps a signed receipt for each of your {list.length} action{list.length === 1 ? '' : 's'}. If the operator ever rewrote
        history, these receipts prove it. Download them now and then.
      </p>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button class="btn ghost" onClick={downloadReceipts} disabled={list.length === 0}>
          Download receipts
        </button>
        {list.length > 0 && (
          <button class="btn ghost" onClick={() => setOpen(!open)}>
            {open ? 'Hide receipts' : 'Show latest receipts'}
          </button>
        )}
      </div>
      {open && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {list.slice(0, 12).map((e) => (
            <Receipt entry={e} key={e.seq} />
          ))}
        </div>
      )}
    </section>
  );
}

function Proof() {
  const s = useStore();
  const [state, setState] = useState<{ ok: boolean; text: string } | null>(null);
  return (
    <section style={{ marginTop: 32 }}>
      <h2 class="display" style={{ fontSize: 36, marginBottom: 8 }}>
        Check your balance
      </h2>
      <p class="muted" style={{ maxWidth: '62ch' }}>
        Confirm that your balance is part of the state the operator signed, using only math in this browser.
      </p>
      <button
        class="btn ghost"
        onClick={async () => {
          setState(null);
          try {
            const p = await api.proof(wallet()!.publicKey);
            const sigOk = s.info ? verifyCheckpointSig(p.checkpoint, s.info.sequencer) : false;
            const inTree = verifyMerkleProof(utf8ToBytes(canonicalize(p.record)), p.proof, p.checkpoint.stateRoot);
            const rec = p.record as { zat: string; tokens: [string, string][] };
            setState(
              sigOk && inTree
                ? { ok: true, text: `Verified: ${zec(rec.zat, 8)} ZEC and ${rec.tokens.length} coin balance(s) are in signed checkpoint #${p.checkpoint.seq}.` }
                : { ok: false, text: 'The proof did not verify. Save your receipts and report this.' },
            );
          } catch (x) {
            setState({ ok: false, text: (x as Error).message });
          }
        }}
      >
        Check my balance
      </button>
      {state && (
        <div class={`notice ${state.ok ? '' : 'bad'}`} style={{ marginTop: 12, color: state.ok ? 'var(--buy)' : undefined }}>
          {state.text}
        </div>
      )}
    </section>
  );
}

function Keys() {
  const [show, setShow] = useState(false);
  const w = wallet()!;
  return (
    <section style={{ marginTop: 32 }}>
      <h2 class="display" style={{ fontSize: 36, marginBottom: 8 }}>
        Recovery key
      </h2>
      <p class="muted">Account {w.publicKey}</p>
      {show ? (
        <>
          <div class="secret">{w.secretKey}</div>
          <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
            <button class="btn ghost" onClick={() => copy(w.secretKey)}>
              Copy key
            </button>
            <button class="btn ghost" onClick={() => setShow(false)}>
              Hide key
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button class="btn ghost" onClick={() => setShow(true)}>
            Show recovery key
          </button>
          <button
            class="btn ghost"
            onClick={() => {
              if (confirm('Remove this wallet from this browser? Without the recovery key you cannot get it back.')) forgetWallet();
            }}
          >
            Remove from this browser
          </button>
        </div>
      )}
    </section>
  );
}
